import { useEffect, useMemo, useState } from 'react';
import { Plus, ArrowLeftRight, Landmark, Wallet, Smartphone, ArrowUpRight, ArrowDownLeft, MoreHorizontal, Check } from 'lucide-react';
import { Money } from '@/shared/display/Money';
import { DateDisplay } from '@/shared/display/DateDisplay';
import { PageHeader } from '@/cajita/ui';
import { CxDialog } from '@/cajita/overlay';
import { useAccounts, useCreateAccount, useMovements, useCreateMovement, useTransfer, type Account } from './treasury.api';

const SOURCE_LABEL: Record<string, string> = { SALE: 'Venta', AP: 'Pago proveedor', EXPENSE: 'Gasto', TRANSFER: 'Transferencia', MANUAL: 'Manual' };
const ACCT_COLORS = ['#7E9BE0', '#5FC6A6', '#E7B86A', '#A892E8', '#84CFEE', '#6FC6BC'];
const fmt = (n: number | string): string =>
  'S/ ' + Number(n).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function AccountIcon({ type, size = 20 }: { type: string; size?: number }): JSX.Element {
  if (type === 'CASH') return <Wallet size={size} />;
  if (type === 'WALLET') return <Smartphone size={size} />;
  return <Landmark size={size} />;
}

export function TreasuryPage(): JSX.Element {
  const { data: accounts } = useAccounts();
  const [sel, setSel] = useState<string>('');
  const [dialog, setDialog] = useState<null | 'account' | 'transfer' | 'movement'>(null);
  const { data: movements } = useMovements(sel || undefined);

  useEffect(() => {
    if (!sel && accounts?.length) setSel(accounts[0]!.id);
  }, [accounts, sel]);

  const total = (accounts ?? []).reduce((s, a) => s + Number(a.balance), 0);
  const flow = useMemo(() => {
    let ingresos = 0;
    let egresos = 0;
    for (const m of movements ?? []) {
      if (m.type === 'IN') ingresos += Number(m.amount);
      else egresos += Number(m.amount);
    }
    return { ingresos, egresos };
  }, [movements]);

  return (
    <div className="cx cx-main" style={{ minHeight: '100%', padding: '28px 32px 64px' }}>
      <div className="page fade-in">
        <PageHeader
          title="Bancos y caja"
          sub="Tesorería · saldos y movimientos"
          actions={
            <>
              <button className="btn btn-secondary" onClick={() => setDialog('transfer')} disabled={(accounts?.length ?? 0) < 2}>
                <ArrowLeftRight size={17} />
                Transferir
              </button>
              <button className="btn btn-secondary" onClick={() => setDialog('movement')} disabled={(accounts?.length ?? 0) === 0}>
                <Plus size={17} />
                Ingreso / Egreso
              </button>
              <button className="btn btn-primary" onClick={() => setDialog('account')}>
                <Plus size={17} />
                Nueva cuenta
              </button>
            </>
          }
        />

        {(accounts?.length ?? 0) === 0 ? (
          <div className="card">
            <div className="empty">
              <div className="empty-ico">
                <Landmark size={28} />
              </div>
              <div className="muted">Crea tu primera cuenta (caja o banco).</div>
              <button className="btn btn-primary" onClick={() => setDialog('account')}>
                <Plus size={17} />
                Nueva cuenta
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(240px,1fr))', gap: 16, marginBottom: 16 }}>
              {(accounts ?? []).map((a, i) => {
                const color = ACCT_COLORS[i % ACCT_COLORS.length]!;
                const on = sel === a.id;
                return (
                  <button
                    key={a.id}
                    onClick={() => setSel(a.id)}
                    className="card card-pad fade-in"
                    style={{
                      position: 'relative',
                      overflow: 'hidden',
                      textAlign: 'left',
                      cursor: 'pointer',
                      border: on ? '1.5px solid var(--primary)' : '1px solid var(--border)',
                      boxShadow: on ? '0 0 0 3px rgba(120,210,240,.22)' : 'var(--sh-sm)',
                    }}
                  >
                    <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: 3, background: color }} />
                    <div className="row between aic" style={{ marginBottom: 16 }}>
                      <div style={{ width: 40, height: 40, borderRadius: 11, display: 'grid', placeItems: 'center', background: `${color}18`, color }}>
                        <AccountIcon type={a.type} />
                      </div>
                      <span className="badge badge-gray">{a.type === 'CASH' ? 'Caja' : a.type === 'WALLET' ? 'Billetera' : a.bank ?? 'Banco'}</span>
                    </div>
                    <div className="t-sm muted fw6">{a.name}</div>
                    <div className="t-h1 tnum fw8" style={{ marginTop: 4 }}>
                      <Money value={a.balance} />
                    </div>
                    {a.number && <div className="t-xs muted tnum" style={{ marginTop: 4 }}>{a.number}</div>}
                  </button>
                );
              })}
            </div>

            <div
              className="card card-pad"
              style={{ background: 'var(--ink)', color: '#fff', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}
            >
              <div>
                <div className="t-sm" style={{ color: '#94A3B8' }}>
                  Saldo total disponible
                </div>
                <div className="t-display tnum" style={{ color: '#fff', marginTop: 4 }}>
                  {fmt(total)}
                </div>
              </div>
              <div className="row gap24">
                <div>
                  <div className="t-xs" style={{ color: '#94A3B8' }}>
                    Ingresos (cuenta)
                  </div>
                  <div className="tnum fw7" style={{ color: '#84CC16' }}>
                    + {fmt(flow.ingresos)}
                  </div>
                </div>
                <div>
                  <div className="t-xs" style={{ color: '#94A3B8' }}>
                    Egresos (cuenta)
                  </div>
                  <div className="tnum fw7" style={{ color: '#F87171' }}>
                    − {fmt(flow.egresos)}
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-head">
                <div className="card-title">Libro de movimientos</div>
                <span className="t-sm muted">{(accounts ?? []).find((a) => a.id === sel)?.name ?? 'Cuenta'}</span>
              </div>
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Concepto</th>
                      <th>Origen</th>
                      <th className="right">Monto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(movements ?? []).map((m) => (
                      <tr key={m.id}>
                        <td className="tnum muted">
                          <DateDisplay value={m.date} />
                        </td>
                        <td>
                          <div className="row aic gap10">
                            <div
                              style={{
                                width: 30,
                                height: 30,
                                borderRadius: 8,
                                display: 'grid',
                                placeItems: 'center',
                                background: m.type === 'IN' ? 'var(--success-soft)' : 'var(--error-soft)',
                                color: m.type === 'IN' ? '#15803D' : '#B91C1C',
                                flex: 'none',
                              }}
                            >
                              {m.type === 'IN' ? <ArrowDownLeft size={15} /> : <ArrowUpRight size={15} />}
                            </div>
                            <span className="strong">{m.description ?? '—'}</span>
                          </div>
                        </td>
                        <td>
                          <span className="badge badge-gray">{SOURCE_LABEL[m.source] ?? m.source}</span>
                        </td>
                        <td className={`right tnum fw7 ${m.type === 'IN' ? 'pos' : 'neg'}`}>
                          {m.type === 'IN' ? '+ ' : '− '}
                          <Money value={m.amount} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(movements ?? []).length === 0 && (
                  <div className="empty">
                    <div className="empty-ico">
                      <MoreHorizontal size={26} />
                    </div>
                    <div className="muted">Sin movimientos en esta cuenta.</div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {dialog === 'account' && <NewAccountDialog onClose={() => setDialog(null)} />}
      {dialog === 'transfer' && <TransferDialog accounts={accounts ?? []} onClose={() => setDialog(null)} />}
      {dialog === 'movement' && <MovementDialog accounts={accounts ?? []} defaultAccount={sel} onClose={() => setDialog(null)} />}
    </div>
  );
}

function NewAccountDialog({ onClose }: { onClose: () => void }): JSX.Element {
  const create = useCreateAccount();
  const [f, setF] = useState({ type: 'BANK', name: '', bank: '', number: '', openingBalance: '0' });
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    create.mutate(
      { type: f.type, name: f.name.trim(), bank: f.bank.trim() || undefined, number: f.number.trim() || undefined, openingBalance: Number(f.openingBalance) || 0 },
      { onSuccess: () => onClose() },
    );
  };
  return (
    <CxDialog
      title="Nueva cuenta"
      icon={<Landmark size={20} />}
      iconTone="primary"
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-secondary grow" onClick={onClose}>
            Cancelar
          </button>
          <button className="btn btn-primary grow" type="submit" form="tre-account" disabled={create.isPending || !f.name.trim()}>
            <Check size={18} />
            {create.isPending ? 'Creando…' : 'Crear cuenta'}
          </button>
        </>
      }
    >
      <form id="tre-account" onSubmit={submit} className="col gap14">
        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="field">
            <label className="label">Tipo de cuenta</label>
            <select className="select" value={f.type} onChange={(e) => setF((s) => ({ ...s, type: e.target.value }))}>
              <option value="BANK">Banco</option>
              <option value="CASH">Caja</option>
              <option value="WALLET">Billetera digital</option>
            </select>
          </div>
          <div className="field">
            <label className="label">Saldo inicial</label>
            <input className="input tnum" type="number" step="any" value={f.openingBalance} onChange={(e) => setF((s) => ({ ...s, openingBalance: e.target.value }))} />
          </div>
        </div>
        <div className="field">
          <label className="label">Nombre</label>
          <input className="input" value={f.name} placeholder="Cta. Corriente BCP" required onChange={(e) => setF((s) => ({ ...s, name: e.target.value }))} />
        </div>
        {f.type === 'BANK' && (
          <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="field">
              <label className="label">Banco</label>
              <input className="input" value={f.bank} placeholder="BCP" onChange={(e) => setF((s) => ({ ...s, bank: e.target.value }))} />
            </div>
            <div className="field">
              <label className="label">N° cuenta</label>
              <input className="input tnum" value={f.number} placeholder="XXX-XXXXXXX-X-XX" onChange={(e) => setF((s) => ({ ...s, number: e.target.value }))} />
            </div>
          </div>
        )}
      </form>
    </CxDialog>
  );
}

function TransferDialog({ accounts, onClose }: { accounts: Account[]; onClose: () => void }): JSX.Element {
  const transfer = useTransfer();
  const [f, setF] = useState({ from: accounts[0]?.id ?? '', to: accounts[1]?.id ?? '', amount: '', description: '' });
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    transfer.mutate(
      { fromAccountId: f.from, toAccountId: f.to, amount: Number(f.amount), description: f.description || undefined },
      { onSuccess: () => onClose() },
    );
  };
  return (
    <CxDialog
      title="Transferencia entre cuentas"
      icon={<ArrowLeftRight size={20} />}
      iconTone="info"
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-secondary grow" onClick={onClose}>
            Cancelar
          </button>
          <button className="btn btn-primary grow" type="submit" form="tre-transfer" disabled={transfer.isPending || !f.from || !f.to || Number(f.amount) <= 0}>
            <Check size={18} />
            {transfer.isPending ? 'Transfiriendo…' : 'Transferir'}
          </button>
        </>
      }
    >
      <form id="tre-transfer" onSubmit={submit} className="col gap14">
        <div className="field">
          <label className="label">Desde</label>
          <select className="select" value={f.from} onChange={(e) => setF((s) => ({ ...s, from: e.target.value }))}>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </div>
        <div className="row center">
          <div style={{ width: 36, height: 36, borderRadius: 99, background: 'var(--canvas)', display: 'grid', placeItems: 'center', color: 'var(--muted)' }}>
            <ArrowDownLeft size={18} />
          </div>
        </div>
        <div className="field">
          <label className="label">Hacia</label>
          <select className="select" value={f.to} onChange={(e) => setF((s) => ({ ...s, to: e.target.value }))}>
            {accounts.filter((a) => a.id !== f.from).map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label className="label">Monto</label>
          <input className="input tnum" type="number" step="any" min="0" value={f.amount} placeholder="0.00" required onChange={(e) => setF((s) => ({ ...s, amount: e.target.value }))} />
        </div>
      </form>
    </CxDialog>
  );
}

function MovementDialog({ accounts, defaultAccount, onClose }: { accounts: Account[]; defaultAccount: string; onClose: () => void }): JSX.Element {
  const create = useCreateMovement();
  const [f, setF] = useState({ accountId: defaultAccount || accounts[0]?.id || '', type: 'IN' as 'IN' | 'OUT', amount: '', description: '' });
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    create.mutate(
      { accountId: f.accountId, type: f.type, amount: Number(f.amount), description: f.description || undefined },
      { onSuccess: () => onClose() },
    );
  };
  return (
    <CxDialog
      title="Registrar movimiento"
      icon={<Wallet size={20} />}
      iconTone="primary"
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-secondary grow" onClick={onClose}>
            Cancelar
          </button>
          <button className="btn btn-primary grow" type="submit" form="tre-mov" disabled={create.isPending || Number(f.amount) <= 0}>
            <Check size={18} />
            {create.isPending ? 'Guardando…' : 'Registrar'}
          </button>
        </>
      }
    >
      <form id="tre-mov" onSubmit={submit} className="col gap14">
        <div className="field">
          <label className="label">Tipo</label>
          <div className="seg" style={{ width: '100%' }}>
            <button type="button" className={f.type === 'IN' ? 'on' : ''} style={{ flex: 1 }} onClick={() => setF((s) => ({ ...s, type: 'IN' }))}>
              Ingreso
            </button>
            <button type="button" className={f.type === 'OUT' ? 'on' : ''} style={{ flex: 1 }} onClick={() => setF((s) => ({ ...s, type: 'OUT' }))}>
              Egreso
            </button>
          </div>
        </div>
        <div className="field">
          <label className="label">Cuenta</label>
          <select className="select" value={f.accountId} onChange={(e) => setF((s) => ({ ...s, accountId: e.target.value }))}>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label className="label">Monto</label>
          <input className="input tnum" type="number" step="any" min="0" value={f.amount} placeholder="0.00" required onChange={(e) => setF((s) => ({ ...s, amount: e.target.value }))} />
        </div>
        <div className="field">
          <label className="label">Concepto</label>
          <input className="input" value={f.description} placeholder="Descripción del movimiento" onChange={(e) => setF((s) => ({ ...s, description: e.target.value }))} />
        </div>
      </form>
    </CxDialog>
  );
}
